/**
 * astroHome — the thin FRONTEND callable seam for the GROUP + CONTACT
 * Astro-Vastu surfaces (the consultation hub on GroupDetail and the Astro tab on
 * ClientDetail). It is the sibling of `astroVastu.ts` (the per-PROJECT seam) and
 * deliberately mirrors its defensive pattern: `httpsCallable`, a pure normaliser
 * that coerces anything unexpected to safe defaults, and a rejected/failed call
 * that resolves to a clean "unavailable" state (`null`) — NEVER a throw.
 *
 * ── DEPLOY REALITY (important) ────────────────────────────────────────────────
 * The two callables below (`resolveAstroGroup`, `renderAstroPdf`) live on Forge's
 * ISOLATED functions branch (`feat/astro-vastu-home`) and are NOT registered /
 * deployed yet. Until they deploy, EVERY call rejects with `functions/not-found`.
 * That is expected: this seam catches it and degrades to `null` (resolve) or
 * `null` (pdf), so the surfaces render their honest pending state and no button
 * is ever a dead control or a white-screen. Once Forge deploys, the SAME wiring
 * lights up with real data with no further frontend change.
 *
 * ── ONE-LINE DEPLOY SEAM ──────────────────────────────────────────────────────
 * The registered callable names are centralised in the two consts below. If
 * Forge's deployed export names differ from `avbp_resolve_astro_group.js` /
 * `avbp_render_astro_pdf.js`, this is a ONE-LINE fix at deploy — nothing else
 * changes.
 *
 * Region + emulator wiring is inherited from the shared `functions` client
 * (asia-south1), identical to every other callable seam.
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import {
  normalizeGroupResponse,
  extractPdfUrl,
  type AstroGroupResolved,
  type RawGroupResponse,
} from './astroHomeNormalize';

export type { AstroGroupMember, AstroGroupResolved, AstroBoxRow } from './astroHomeNormalize';

/* ════════════════════ ONE-LINE DEPLOY SEAM ════════════════════ */

/**
 * The registered name of the group/contact astro resolver. If Forge's deployed
 * export differs from this, change THIS ONE LINE at deploy — nothing else does.
 * (Registered from `avbp_resolve_astro_group.js`.)
 */
export const RESOLVE_ASTRO_GROUP_FN = 'resolveAstroGroup';

/**
 * The registered name of the astro PDF renderer. Same one-line-fix rule.
 * (Registered from `avbp_render_astro_pdf.js`.)
 */
export const RENDER_ASTRO_PDF_FN = 'renderAstroPdf';

/* ════════════════════ Target discriminator ════════════════════ */

/**
 * Who the astro view is for: a whole family/organisation (`groupId`) OR an
 * individual / couple derived from Contacts (`contactIds`). Both callables
 * accept either shape.
 */
export type AstroTarget = { groupId: string } | { contactIds: string[] };

/**
 * Which canonical document the renderer should return:
 *   · `'chart'`       — a PERSON's 2-page Bhava Chakra birth chart (target must
 *                       carry `contactIds`).
 *   · `'astro-vastu'` — a GROUP's 23-page Astro-Vastu Report (target must carry
 *                       `groupId`).
 * Passed EXPLICITLY at every call site — never inferred — so the backend serves
 * the correct canonical PDF for the surface.
 */
export type AstroKind = 'astro-vastu' | 'chart';

/** The full render payload: a target plus the explicit document `kind`. */
export type AstroRenderTarget = AstroTarget & { kind: AstroKind };

/** True when the target carries nothing to resolve (guards an empty call). */
function targetEmpty(t: AstroTarget): boolean {
  if ('groupId' in t) return !t.groupId;
  return !Array.isArray(t.contactIds) || t.contactIds.filter(Boolean).length === 0;
}

/* ════════════════════ The callables ════════════════════ */

const _resolveAstroGroup = httpsCallable<
  AstroTarget & { recalc?: boolean },
  RawGroupResponse
>(functions, RESOLVE_ASTRO_GROUP_FN);

const _renderAstroPdf = httpsCallable<
  AstroRenderTarget,
  {
    url?: unknown;
    pdfUrl?: unknown;
    pdf_url?: unknown;
    downloadURL?: unknown;
    storagePath?: unknown;
    bytes?: unknown;
    kind?: unknown;
    astroOutputVersion?: unknown;
  }
>(functions, RENDER_ASTRO_PDF_FN);

/**
 * Resolve the Astro-Vastu view for a group OR an individual/couple. Returns
 * `null` on ANY failure (network, auth, backend error, or the not-yet-deployed
 * callable → `functions/not-found`) so the surface renders a clean honest
 * pending state instead of throwing. NEVER rejects.
 *
 * @param target  `{ groupId }` for a family/org, or `{ contactIds }` for a person.
 * @param opts    `{ recalc: true }` to force the engine to recompute.
 */
export async function resolveAstroGroup(
  target: AstroTarget,
  opts?: { recalc?: boolean },
): Promise<AstroGroupResolved | null> {
  if (targetEmpty(target)) return null;
  try {
    const payload = opts?.recalc ? { ...target, recalc: true } : { ...target };
    const res = await _resolveAstroGroup(payload);
    return normalizeGroupResponse(res.data);
  } catch {
    // Callable not deployed yet (Forge's isolated branch) OR a transient
    // failure — degrade to null so the tab shows its honest pending state.
    return null;
  }
}

/**
 * Render (or fetch the cached) Astro-Vastu PDF for a group OR an
 * individual/couple. Returns the PDF URL, or `null` on ANY failure (including
 * the not-yet-deployed callable) so the caller can toast an honest "not
 * available yet" instead of crashing. NEVER rejects.
 *
 * @param target  a target (`{ groupId }` or `{ contactIds }`) PLUS the explicit
 *                document `kind` — `'chart'` for a person's Bhava Chakra,
 *                `'astro-vastu'` for a group's Astro-Vastu Report.
 */
export async function renderAstroPdf(target: AstroRenderTarget): Promise<string | null> {
  if (targetEmpty(target)) return null;
  try {
    const res = await _renderAstroPdf(target);
    return extractPdfUrl(res.data);
  } catch {
    return null;
  }
}
